import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material'
import React, { Fragment, useEffect, useState, useCallback } from 'react'
import NotificationItemList from '../notificationItemList'
import { useTranslation } from 'react-i18next'
import { useCustomNavigation } from '../../../hooks/useCustomNavigate'
import { INotifications } from '../../../utils/types'
import instance from '../../../providers/axios.config'
import Notiflix from 'notiflix'
import { ROUTES } from '../../../utils/constants'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../redux/reducers/rootReducer'
import { updateNotifications } from '../../../redux/reducers/usersReducer'
import { setCountNotifications } from '../../../redux/reducers/notificationsReducer'
import styles from './Unread.module.scss'
import LoadingNotification from './LoadingNotification'

type UnreadType = {
  isHeader?: boolean
}

export const Unread = (props: UnreadType) => {
  const { isHeader } = props
  const { t } = useTranslation()
  const customNavigate = useCustomNavigation()
  const dispatch = useDispatch()
  
  const notificationsReducer = useSelector((state: RootState) => state.notifications)
  const profile = useSelector((state: RootState) => state.user.usersData)

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [notificationsUnread, setNotificationsUnread] = useState<INotifications[]>([])
  const [notificationsUnreadOld, setNotificationsUnreadOld] = useState<INotifications[]>([])
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 50,
    totalPages: 1
  })

  const fetchNotificationsUnread = useCallback(async (page: number, append: boolean = false) => {
    if (page === 1) {
      setIsLoading(true)
    } else {
      setIsLoadingMore(true)
    }

    try {
      const res = await instance.get(`notifications/unread?limit=${pagination.perPage}&page=${page}`) as any
      
      if (append) {
        // Concatenate new results with existing ones
        setNotificationsUnread(prev => [...prev, ...(res.data.notifications.new || [])])
        setNotificationsUnreadOld(prev => [...prev, ...(res.data.notifications.old || [])])
      } else {
        setNotificationsUnread(res.data.notifications.new || [])
        setNotificationsUnreadOld(res.data.notifications.old || [])
      }
      
      setPagination({ ...res?.data?.pagination })
    } catch (error) {
      console.error('Error fetching data:', error)
      Notiflix.Report.failure(
        'Error',
        'Failed to fetch data. Please try again later.',
        'OK'
      )
    } finally {
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  }, [pagination.perPage])

  useEffect(() => {
    fetchNotificationsUnread(1, false)
  }, [])

  const handleShowMore = () => {
    if (pagination.currentPage < pagination.totalPages) {
      fetchNotificationsUnread(pagination.currentPage + 1, true)
    }
  }

  const handleReadNotification = async (id: number) => {
    Notiflix.Loading.standard({ svgColor: '#c11b1e' })
    try {
      const resp = await instance.put(`notifications/${id}`) as any
      if (resp.status === 'success') {
        const updateNotificationOld = notificationsUnreadOld.map((not) => {
          if (not.id === id) {
            return { ...not, isRead: 1 }
          }
          return { ...not }
        })
        const updateNotificationNew = notificationsUnread.map((not) => {
          if (not.id === id) {
            return { ...not, isRead: 1 }
          }
          return { ...not }
        })
        setNotificationsUnreadOld([...updateNotificationOld])
        setNotificationsUnread([...updateNotificationNew])
        
        // Decrease unread count
        dispatch(setCountNotifications(notificationsReducer.count - 1))
        
        Notiflix.Loading.remove()
      } else {
        Notiflix.Loading.remove()
        Notiflix.Report.failure('Error', `${resp?.message}`, 'OK')
      }
    } catch (e) {
      Notiflix.Loading.remove()
      Notiflix.Report.failure('Error', `${t('error_api')}`, 'OK')
    }
  }

  const handleDeleteNotification = async (id: number) => {
    Notiflix.Loading.standard({ svgColor: '#c11b1e' })
    try {
      const resp = await instance.delete(`notifications/${id}`) as any
      if (resp.status === 'success') {
        // Find the notification to check if it was unread
        const notificationOld = notificationsUnreadOld.find((notification) => notification.id === id)
        const notificationNew = notificationsUnread.find((notification) => notification.id === id)
        const currentNotification = notificationOld || notificationNew
        
        // Filter out the deleted notification
        const updateNotificationOld = notificationsUnreadOld.filter((notification) => notification.id !== id)
        const updateNotificationNew = notificationsUnread.filter((notification) => notification.id !== id)
        setNotificationsUnreadOld([...updateNotificationOld])
        setNotificationsUnread([...updateNotificationNew])

        // Decrease count only if the deleted notification was unread
        if (currentNotification && currentNotification.isRead === 0) {
          dispatch(setCountNotifications(notificationsReducer.count - 1))
        }
        
        Notiflix.Loading.remove()
        Notiflix.Notify.success(`${resp?.message}`)
      } else {
        Notiflix.Loading.remove()
        Notiflix.Report.failure('Error', `${resp?.message}`, 'OK')
      }
    } catch (e) {
      Notiflix.Loading.remove()
      Notiflix.Report.failure('Error', `${t('error_api')}`, 'OK')
    }
  }

  const handleRedirect = () => {
    customNavigate(ROUTES.NOTIFICATIONS)
  }

  const handleDisabledNotifications = async (type: any) => {
    Notiflix.Loading.standard({ svgColor: '#c11b1e' })
    const findDataNotifications = (profile?.notificationsDisabled || []).find((not: string) => not === type)
    try {
      const resp = await instance.put(
        `notifications/${type}/toggle`,
        JSON.stringify({ action: findDataNotifications ? 'enable' : 'disable' })
      ) as any
      if (resp.status === 'success') {
        if (findDataNotifications) {
          dispatch(updateNotifications((profile?.notificationsDisabled || []).filter((d: string) => d !== type)))
        } else {
          dispatch(updateNotifications(profile?.notificationsDisabled?.length ? [...profile?.notificationsDisabled, type] : [type]))
        }
        Notiflix.Loading.remove()
      } else {
        Notiflix.Loading.remove()
        Notiflix.Report.failure('Error', `${resp?.message}`, 'OK')
      }
    } catch (e) {
      Notiflix.Loading.remove()
      Notiflix.Report.failure('Error', `${t('error_api')}`, 'OK')
    }
  }

  const hasMorePages = pagination.currentPage < pagination.totalPages

  if (isLoading) {
    return (
      <Box className={`${styles.notificationList} ${isHeader ? styles.isHeader : ''}`}>
        {[1, 2, 3, 4].map((number) => (
          <Fragment key={number}>
            <LoadingNotification isHeader={isHeader} />
          </Fragment>
        ))}
      </Box>
    )
  }

  if (!notificationsUnreadOld?.length && !notificationsUnread?.length && !isLoading) {
    return (
      <Box className={`${styles.emptyState} ${isHeader ? styles.isHeader : ''}`}>
        <Typography className={styles.emptyText}>
          {t('no_notification_unread')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box className={`${styles.notificationList} ${isHeader ? styles.isHeader : ''}`}>
      {/* New Unread Notifications Section */}
      {notificationsUnread?.length > 0 && (
        <Box className={styles.section}>
          <Box className={styles.sectionHeader}>
            <Typography className={`${styles.sectionTitle} ${isHeader ? styles.isHeader : ''}`}>
              {t('new')}
            </Typography>
            {isHeader && (
              <Button 
                onClick={handleRedirect} 
                className={styles.seeAllButton}
                size="small"
              >
                {t('see_all')}
              </Button>
            )}
          </Box>
          <Box className={styles.notificationItems}>
            {notificationsUnread.map((not: INotifications, index: number) => (
              <Fragment key={not.id}>
                <NotificationItemList
                  notification={not}
                  isHeader={isHeader}
                  onReadNotification={handleReadNotification}
                  onDeleteNotification={handleDeleteNotification}
                  onDisableNotification={handleDisabledNotifications}
                />
                {index < notificationsUnread.length - 1 && (
                  <Divider className={`${styles.divider} ${isHeader ? styles.isHeader : ''}`} />
                )}
              </Fragment>
            ))}
          </Box>
        </Box>
      )}

      {/* Divider between new and old sections */}
      {notificationsUnread?.length > 0 && notificationsUnreadOld?.length > 0 && (
        <Divider className={`${styles.sectionDivider} ${isHeader ? styles.isHeader : ''}`} />
      )}

      {/* Older Unread Notifications Section */}
      {notificationsUnreadOld?.length > 0 && (
        <Box className={styles.section}>
          <Box className={styles.sectionHeader}>
            <Typography className={`${styles.sectionTitle} ${isHeader ? styles.isHeader : ''}`}>
              {t('older')}
            </Typography>
            {isHeader && !notificationsUnread?.length && (
              <Button 
                onClick={handleRedirect} 
                className={styles.seeAllButton}
                size="small"
              >
                {t('see_all')}
              </Button>
            )}
          </Box>
          <Box className={styles.notificationItems}>
            {notificationsUnreadOld.map((notification: INotifications, index: number) => (
              <Fragment key={notification?.id}>
                <NotificationItemList
                  notification={notification}
                  isHeader={isHeader}
                  onReadNotification={handleReadNotification}
                  onDeleteNotification={handleDeleteNotification}
                  onDisableNotification={handleDisabledNotifications}
                />
                {index < notificationsUnreadOld.length - 1 && (
                  <Divider className={`${styles.divider} ${isHeader ? styles.isHeader : ''}`} />
                )}
              </Fragment>
            ))}
          </Box>
        </Box>
      )}

      {/* Show More Button */}
      {hasMorePages && (
        <Box className={styles.showMoreContainer}>
          <Button
            variant="text"
            onClick={handleShowMore}
            disabled={isLoadingMore}
            className={`${styles.showMoreButton} ${isHeader ? styles.isHeader : ''}`}
          >
            {isLoadingMore ? (
              <CircularProgress size={20} sx={{ color: isHeader ? '#CBC7C2' : '#c11b1e' }} />
            ) : (
              t('show_more')
            )}
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default Unread